import {
  Button,
  Card,
  CardProps,
  Col,
  Divider,
  Empty,
  Input,
  Modal,
  ModalProps,
  Row,
  Skeleton,
  Tabs,
  Typography,
  message,
  Spin,
  Checkbox
} from 'antd';
import React, { lazy, useEffect, useMemo, useState } from 'react';
import {
  CloseOutlined,
  SearchOutlined,
  CheckOutlined,
  ReloadOutlined,
  SaveOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  BoldOutlined
} from '@ant-design/icons';
import { QuantumVendorDetails, SupplierTitle } from 'models/Supplier';
import { useRecoilValue } from 'recoil';
import { selectedSupplierDynamicsVendorState } from 'recoil/atoms';
import { useVendorApi } from 'api/vendor';
import { useSupplierDynamicsApi } from 'api/supplier_dynamics';
import { SupplierBriefingData, SupplierRisk, SupplierRiskData } from 'models/SupplierDynamics';
import { ACLCheck } from 'shared/Helpers';
import { ArticleList } from 'components/documents/ArticleList';
import { AboutSupplier } from 'components/supplier-dynamics/about-supplier/AboutSupplier';
import 'components/supplier-dynamics/SupplierCard.scss';
import DealNotes from 'components/deal-modal/DealNotes';
import { useSearchParams } from 'react-router-dom';

import { SupplierDynamicsVendor } from 'types/global';
import { ENABLE_SD_PRODUCTS } from 'shared/constants';

import { EditorPane } from 'components/markdown-editor';
import { SupplierIQContent } from 'components/supplier-dynamics/SupplierIQContent';
import { SupplierRiskContent } from 'components/supplier-dynamics/SupplierRiskContent';
import { AboutQuantumSupplier } from 'components/supplier-dynamics/about-supplier/AboutQuantumSupplier';
import {
  LEGACY_PURCHASE_CHARACTERISTICS_TABS,
  normalizeSupplierDynamicsTab,
  SUPPLIER_DYNAMICS_TAB_KEYS
} from 'components/supplier-dynamics/constants';
import { SupplierProducts } from 'components/supplier-dynamics/supplier-products/SupplierProducts';
import { ProductIQProductsTab } from 'components/product-iq/products/ProductIQProductsTab';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import { useProductIQApi } from 'api/product_iq';
import { SupplierOverviewData } from 'models/SupplierDynamics';
// ... existing imports ...

const SupplierNews = lazy(() => import('components/news/SupplierNews'));
const SupplierCardTitle = lazy(() => import('components/supplier-dynamics/vendor-card-title/SupplierCardTitle'));

const panelMarginBottom = 24;

// Initialize Turndown service for HTML to Markdown conversion
const turndownService = new TurndownService({
  codeBlockStyle: 'fenced',
  headingStyle: 'atx'
});
turndownService.use(gfm);

/**
 * Normalizes HTML tables and inline wrappers before markdown conversion.
 * @param {string} raw - Raw HTML string.
 * @returns {string} Normalized HTML string.
 */
const normalizeTableHtml = (raw: string): string => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(raw, 'text/html');

    // Remove colgroups entirely
    doc.querySelectorAll('colgroup').forEach((group) => group.remove());

    // Clean tables: drop empty rows and remove table if nothing meaningful remains
    doc.querySelectorAll('table').forEach((table) => {
      const rows = Array.from(table.querySelectorAll('tr'));

      rows.forEach((row) => {
        const cells = Array.from(row.querySelectorAll('th,td'));
        const hasContent = cells.some((cell) => {
          const cleanText = (cell.textContent ?? '').replace(/\u00A0/g, '').trim();
          const hasMedia = cell.querySelector('img,svg,video,canvas,object,embed,iframe') !== null;
          const hasOnlyBreaks =
            !cleanText &&
            cell.innerHTML
              .replace(/<br\s*\/?>/gi, '')
              .replace(/&nbsp;/gi, '')
              .trim().length === 0;
          return (cleanText.length > 0 || hasMedia) && !hasOnlyBreaks;
        });

        if (!hasContent) {
          row.remove();
        }
      });

      const meaningfulCells = Array.from(table.querySelectorAll('th,td')).filter((cell) => {
        const text = cell.textContent?.trim() ?? '';
        const hasMedia = cell.querySelector('img,svg,video,canvas,object,embed') !== null;
        return text.length > 0 || hasMedia;
      });

      if (meaningfulCells.length === 0) {
        table.remove();
      }
    });

    // Strip paragraph wrappers and collapse whitespace between tags
    return doc.body.innerHTML
      .replace(/<p>([\s\S]*?)<\/p>/gi, '$1')
      .replace(/>\s+</g, '><')
      .trim();
  } catch (_err) {
    // Fallback to lightweight normalization
    return raw
      .replace(/<colgroup[\s\S]*?<\/colgroup>/gi, '')
      .replace(/<p>([\s\S]*?)<\/p>/gi, '$1')
      .replace(/>\s+</g, '><')
      .trim();
  }
};

// ... existing EditProductIQModal code ...
export interface SupplierOption {
  id: number;
  name: string;
  vendor_domain?: string;
}

export interface SupplierContentEditorModalProps extends ModalProps {
  suppliers?: SupplierOption[];
  selectedSupplierId?: number;
  onSupplierSelect?: (supplierId: number) => void;
  initialContent?: string;
  headerTitle?: string;
  panelTitle?: string;
  showSidebar?: boolean;
  onSave?: (content: string) => Promise<void>;
  isSaving?: boolean;
}

export const SupplierContentEditorModal: React.FC<SupplierContentEditorModalProps> = ({
  open,
  onCancel,
  suppliers = [],
  selectedSupplierId,
  initialContent = '',
  headerTitle,
  panelTitle,
  showSidebar = false,
  onSupplierSelect,
  onSave,
  isSaving = false,
  ...rest
}) => {
  const [supplierContent, setSupplierContent] = useState<string>(initialContent);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<number | undefined>(selectedSupplierId);
  const [hasInformation, setHasInformation] = useState<boolean>(false);
  const [isBulkInsertMode, setIsBulkInsertMode] = useState<boolean>(false);
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setSupplierContent(initialContent);
    setIsDirty(false);
  }, [initialContent, open]);

  useEffect(() => {
    if (open) {
      setSelectedSupplier(selectedSupplierId);
    }
  }, [selectedSupplierId, open]);

  const handleSaveChanges = async () => {
    if (onSave) {
      await onSave(supplierContent);
      setIsDirty(false);
    } else {
      console.log('Save Changes');
      setIsDirty(false);
    }
  };

  const handleContentChange = (content: string) => {
    setSupplierContent(content);
    setIsDirty(true);
  };

  // Sidebar specific logic
  const effectiveSuppliers = suppliers.length > 0 ? suppliers : [];

  const filteredSuppliers = effectiveSuppliers.filter((supplier) =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSupplierClick = (supplierId: number) => {
    setSelectedSupplier(supplierId);
    onSupplierSelect?.(supplierId);

    // Mock behavior for demo
    const hasInfo = false;
    setHasInformation(hasInfo);

    if (!hasInfo) {
      const supplier = effectiveSuppliers.find((s) => s.id === supplierId);
      setSupplierContent(
        `<div style="color: #ff4d4f; display: flex; align-items: center; gap: 8px; justify-content: center;"><span style="color: #ff4d4f; font-size: 18px;">⚠</span> Could not find any information about ${supplier?.name}</div>`
      );
    } else {
      setSupplierContent('');
    }
    setIsDirty(false);
  };

  const selectedSupplierData =
    effectiveSuppliers.find((supplier) => supplier.id === (showSidebar ? selectedSupplier : selectedSupplierId)) ??
    (effectiveSuppliers[0] ? effectiveSuppliers[0] : undefined);

  const displayTitle = headerTitle ?? 'Edit Product IQ';
  const panelDisplayTitle =
    panelTitle ?? (selectedSupplierData ? `${selectedSupplierData.name} : Product IQ Information` : displayTitle);

  if (showSidebar) {
    return (
      <Modal
        title={<span className="text-[18px] font-bold text-[#102542]">{displayTitle}</span>}
        open={open}
        onCancel={onCancel}
        footer={null}
        width={1200}
        centered={true}
        className="edit-product-iq-modal"
        closable={false}
        {...rest}
      >
        <div className="flex h-[600px] bg-gray-100">
          {/* Left Panel - Supplier List */}
          <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
            {/* Header */}
            <div className="bg-[#9CA3AF] px-4 py-3 flex items-center justify-between shrink-0 h-[56px]">
              <span className="text-[16px] font-bold text-white">Supplier Name</span>
              <div
                className={`flex items-center justify-center gap-2 rounded-md p-1 border ${
                  isBulkInsertMode ? 'bg-gray-700 border-white/40' : 'bg-gray-600 border-white/20'
                }`}
              >
                <Checkbox
                  checked={isBulkInsertMode}
                  onChange={(e) => {
                    setIsBulkInsertMode(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedVendorIds(new Set());
                    }
                  }}
                  className="text-white"
                >
                  <span className="text-white text-sm font-medium">Bulk Insert</span>
                </Checkbox>
              </div>
            </div>
            <div className="p-4 border-b border-gray-200">
              <Input
                placeholder="Search"
                suffix={<SearchOutlined className="text-gray-400" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md"
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredSuppliers.map((supplier) => (
                <div
                  key={supplier.id}
                  onClick={() => handleSupplierClick(supplier.id)}
                  className={`px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 flex items-center justify-between ${
                    selectedSupplier === supplier.id ? 'bg-gray-100' : ''
                  }`}
                >
                  <span
                    className={`text-sm ${
                      selectedSupplier === supplier.id ? 'font-medium text-gray-900' : 'text-gray-700'
                    }`}
                  >
                    {supplier.name}
                  </span>
                  {selectedSupplier === supplier.id && (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckOutlined className="text-white" style={{ fontSize: '12px' }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Supplier IQ Information */}
          <div className="flex-1 flex flex-col bg-white">
            <div className="bg-[#102542] px-5 py-3 flex items-center h-[56px] shrink-0">
              <span className="text-white font-medium text-[15px]">{panelDisplayTitle}</span>
            </div>

            <div className="flex-1 overflow-y-auto bg-white p-0">
              <EditorPane
                markdown={supplierContent}
                onMarkdownChange={handleContentChange}
                editable={true}
                showToolbar
              />
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <Button
                type="default"
                icon={<ReloadOutlined className="text-[#0070FF]" />}
                className="text-[#0070FF] border-[#0070FF] hover:border-[#0056CC] hover:text-[#0056CC] bg-white font-medium"
              >
                Run/Refresh Product IQ
              </Button>

              <div className="flex items-center gap-3">
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  className="text-gray-500 hover:text-gray-700 hover:bg-transparent"
                  onClick={() => setSupplierContent('')}
                >
                  Reset/Clear
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  className="!bg-gray-300 !border-gray-300 !text-white font-medium hover:!bg-gray-400"
                  onClick={handleSaveChanges}
                  disabled={!isDirty || isSaving}
                  loading={isSaving}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="py-3 px-6 flex justify-end bg-white border-t border-gray-200 rounded-b-lg">
          <Button
            type="default"
            className="bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 font-medium px-6"
            onClick={onCancel}
          >
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={<span className="text-[18px] font-bold text-[#102542]">{displayTitle}</span>}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={900}
      centered={true}
      className="edit-product-iq-modal"
      closable={false}
      {...rest}
    >
      <div className="flex flex-col h-[600px] bg-white rounded-lg overflow-hidden">
        <div className="bg-[#102542] px-5 py-3 flex items-center h-[56px] shrink-0">
          <span className="text-white font-medium text-[15px]">{panelDisplayTitle}</span>
        </div>

        <div className="flex-1 overflow-y-auto bg-white p-0">
          <EditorPane markdown={supplierContent} onMarkdownChange={handleContentChange} editable={true} showToolbar />
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
          <Button
            type="default"
            className="bg-gray-100 border-gray-300 text-gray-700 font-medium px-6"
            onClick={onCancel}
          >
            Close
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            className="!bg-[#102542] !border-[#102542] !text-white font-medium"
            onClick={handleSaveChanges}
            disabled={!isDirty || isSaving}
            loading={isSaving}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const CustomScanModal: React.FC<SupplierContentEditorModalProps & { onRefreshComplete?: () => void }> = ({
  open,
  onCancel,
  suppliers = [],
  selectedSupplierId,
  onSupplierSelect,
  onRefreshComplete,
  ...rest
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<number | undefined>(selectedSupplierId);
  const [supplierContent, setSupplierContent] = useState<string>('');
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isLoadingOverview, setIsLoadingOverview] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [overviewData, setOverviewData] = useState<SupplierOverviewData | null>(null);
  const [isBulkInsertMode, setIsBulkInsertMode] = useState<boolean>(false);
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<number>>(new Set());
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      setIsBulkInsertMode(false);
      setSelectedVendorIds(new Set());
      setSearchTerm('');
      setSelectedSupplier(selectedSupplierId);
      setSupplierContent('');
      setOverviewData(null);
      setIsDirty(false);
    }
  }, [open, selectedSupplierId]);

  // API hooks
  const { fetchProductIQOverview, updateProductIQOverview } = useProductIQApi();
  const { startVendorDataRefresh } = useVendorApi();

  // Use suppliers prop if provided, otherwise use mock data
  const effectiveSuppliers = suppliers.length > 0 ? suppliers : [];

  /**
   * Handles supplier selection and fetches overview data
   * @param supplierId
   */
  const handleSupplierClick = async (supplierId: number) => {
    setSelectedSupplier(supplierId);
    onSupplierSelect?.(supplierId);
    setSupplierContent('');
    setOverviewData(null);
    setIsDirty(false);

    const supplier = effectiveSuppliers.find((s) => s.id === supplierId);
    if (!supplier?.vendor_domain) {
      setSupplierContent(
        `<div style="color: #ff4d4f; display: flex; align-items: center; gap: 8px; justify-content: center;"><span style="color: #ff4d4f; font-size: 18px;">⚠</span> No vendor domain available for ${supplier?.name}</div>`
      );
      return;
    }

    // Fetch Product IQ overview data
    setIsLoadingOverview(true);
    try {
      const overview = await fetchProductIQOverview(supplier.vendor_domain);
      if (overview) {
        setOverviewData(overview);
        setSupplierContent(overview.overview || '');
      } else {
        setSupplierContent(
          `<div style="color: #ff4d4f; display: flex; align-items: center; gap: 8px; justify-content: center;"><span style="color: #ff4d4f; font-size: 18px;">⚠</span> Could not find any information about ${supplier?.name}</div>`
        );
      }
    } catch (error) {
      console.error('Error fetching Product IQ overview:', error);
      setSupplierContent(
        `<div style="color: #ff4d4f; display: flex; align-items: center; gap: 8px; justify-content: center;"><span style="color: #ff4d4f; font-size: 18px;">⚠</span> Error loading information for ${supplier?.name}</div>`
      );
    } finally {
      setIsLoadingOverview(false);
    }
  };

  /**
   * Handles saving changes to Product IQ overview
   */
  const handleSaveChanges = async () => {
    const supplier = effectiveSuppliers.find((s) => s.id === selectedSupplier);
    if (!supplier?.vendor_domain || !overviewData) {
      message.error('No vendor or overview data available');
      return;
    }

    setIsSaving(true);
    try {
      // Convert HTML to markdown before sending to API
      const markdownContent = turndownService.turndown(normalizeTableHtml(supplierContent));

      // Call update API
      await updateProductIQOverview(supplier.vendor_domain, {
        company: overviewData.company,
        companyOverview: markdownContent,
        snapshotDate: overviewData.snapshot_date,
        sources: overviewData.sources,
        confidence: overviewData.confidence
      });

      message.success('Product IQ overview updated successfully');
      setIsDirty(false);
      onCancel?.(undefined as any);
    } catch (error) {
      console.error('Failed to update Product IQ overview:', error);
      message.error('Failed to update Product IQ overview. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = (content: string) => {
    setSupplierContent(content);
    setIsDirty(true);
  };

  const handleResetContent = () => {
    if (overviewData?.overview) {
      setSupplierContent(overviewData.overview);
      setIsDirty(false);
    }
  };

  /**
   * Handles bulk vendor selection toggle
   * @param vendorId
   * @param checked
   */
  const handleVendorCheckboxChange = (vendorId: number, checked: boolean) => {
    const newSelected = new Set(selectedVendorIds);
    if (checked) {
      newSelected.add(vendorId);
    } else {
      newSelected.delete(vendorId);
    }
    setSelectedVendorIds(newSelected);
  };

  /**
   * Handles select all/deselect all vendors
   * @param checked
   */
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all visible suppliers
      const allIds = new Set(filteredSuppliers.map((supplier) => supplier.id));
      setSelectedVendorIds(allIds);
    } else {
      // Deselect all
      setSelectedVendorIds(new Set());
    }
  };

  /**
   * Handles Run/Refresh Product IQ - calls startVendorDataRefresh with selected vendors
   */
  const handleRunRefresh = async () => {
    let vendorIds: number[] = [];

    if (isBulkInsertMode) {
      vendorIds = Array.from(selectedVendorIds);
      if (vendorIds.length === 0) {
        message.warning('Please select at least one vendor to refresh');
        return;
      }
    } else {
      if (!selectedSupplier) {
        message.warning('Please select a vendor first');
        return;
      }
      vendorIds = [selectedSupplier];
    }

    setIsRefreshing(true);
    try {
      await startVendorDataRefresh({
        syncType: 'PRODUCT_IQ',
        vendorIds
      });
      message.success('Product IQ refresh started successfully');

      // Trigger the refresh callback and close the modal
      if (onRefreshComplete) {
        onRefreshComplete();
      }
      // Close modal by triggering the onCancel callback from parent
      const cancelEvent = new Event('cancel');
      onCancel?.(cancelEvent as any);
    } catch (error) {
      console.error('Error starting Product IQ refresh:', error);
      message.error('Failed to start Product IQ refresh. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredSuppliers = searchTerm
    ? effectiveSuppliers.filter((supplier) => supplier.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : effectiveSuppliers;

  /**
   * Determine if all visible suppliers are selected
   */
  const isAllSelected =
    effectiveSuppliers.length > 0 && filteredSuppliers.every((supplier) => selectedVendorIds.has(supplier.id));

  const selectedSupplierData = effectiveSuppliers.find((s) => s.id === selectedSupplier);

  return (
    <Modal
      title={<span className="text-[18px] font-bold text-[#102542]">Edit Product IQ : Custom Search</span>}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1200}
      className="edit-product-iq-modal"
      closeIcon={
        <div className="flex items-center justify-center h-full">
          <CloseOutlined style={{ marginBottom: 22, fontSize: 18 }} />
        </div>
      }
      {...rest}
    >
      <div className="flex h-[600px] bg-gray-100">
        {/* Left Panel - Supplier List */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="bg-[#9CA3AF] px-4 py-3 flex items-center justify-between shrink-0 h-[56px]">
            <span className="text-[16px] font-bold text-white">Supplier Name</span>
            <div
              className={`flex items-center justify-center gap-2 border ${
                isBulkInsertMode ? 'bg-gray-700' : 'bg-gray-600'
              } rounded-md p-1 `}
            >
              <Checkbox
                checked={isBulkInsertMode}
                onChange={(e) => {
                  setIsBulkInsertMode(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedVendorIds(new Set());
                  }
                }}
                className="text-white"
              >
                <span className="text-white text-medium font-light">Bulk Insert</span>
              </Checkbox>
            </div>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <Input
              placeholder="Search"
              suffix={<SearchOutlined className="text-gray-400" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md"
            />
          </div>

          {/* Instruction */}
          {effectiveSuppliers.length === 0 && (
            <div className="p-4 flex items-center gap-2">
              <ArrowUpOutlined className="text-gray-500" />
              <span className="text-gray-600">No vendors available. Save settings to filter vendors.</span>
            </div>
          )}

          {/* Supplier List */}
          <div className="flex-1 overflow-y-auto">
            {/* Select All Row - Only shown in bulk insert mode */}
            {isBulkInsertMode && filteredSuppliers.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2 sticky top-0">
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="text-sm font-medium text-gray-900">Select All</span>
              </div>
            )}

            {filteredSuppliers.length > 0 ? (
              filteredSuppliers.map((supplier) => (
                <div
                  key={supplier.id}
                  onClick={() => {
                    if (isBulkInsertMode) {
                      handleVendorCheckboxChange(supplier.id, !selectedVendorIds.has(supplier.id));
                    } else {
                      handleSupplierClick(supplier.id);
                    }
                  }}
                  className={`px-4 py-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 flex items-center justify-between ${
                    isBulkInsertMode
                      ? selectedVendorIds.has(supplier.id)
                        ? 'bg-blue-50'
                        : ''
                      : selectedSupplier === supplier.id
                        ? 'bg-gray-100'
                        : ''
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    {isBulkInsertMode && (
                      <Checkbox
                        checked={selectedVendorIds.has(supplier.id)}
                        onChange={() => handleVendorCheckboxChange(supplier.id, !selectedVendorIds.has(supplier.id))}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <span
                      className={`text-sm ${
                        isBulkInsertMode
                          ? selectedVendorIds.has(supplier.id)
                            ? 'font-medium text-gray-900'
                            : 'text-gray-700'
                          : selectedSupplier === supplier.id
                            ? 'font-medium text-gray-900'
                            : 'text-gray-700'
                      }`}
                    >
                      {supplier.name}
                    </span>
                  </div>
                  {!isBulkInsertMode && selectedSupplier === supplier.id && (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <CheckOutlined className="text-white" style={{ fontSize: '12px' }} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500 text-sm">No vendors available</div>
            )}
          </div>
        </div>

        {/* Right Panel - Supplier IQ Information */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Header Bar */}
          <div className="bg-[#102542] px-5 py-3 flex items-center h-[56px] shrink-0">
            <span className="text-white font-medium text-[15px]">
              {selectedSupplierData ? `${selectedSupplierData.name} : Product IQ Information` : 'Select a supplier'}
            </span>
          </div>

          {/* Content Area with Loading State */}
          <div className="flex-1 overflow-y-auto p-0 bg-white relative">
            {isLoadingOverview && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10">
                <Spin />
              </div>
            )}
            <EditorPane
              markdown={selectedSupplierData ? supplierContent : ''}
              onMarkdownChange={handleContentChange}
              editable={!!selectedSupplierData && !!overviewData}
              showToolbar={true}
            />
          </div>

          {/* Bottom Actions */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <Button
              type="default"
              icon={<ReloadOutlined className="text-[#0070FF]" />}
              className="text-[#0070FF] border-[#0070FF] hover:border-[#0056CC] hover:text-[#0056CC] bg-white font-medium"
              onClick={handleRunRefresh}
              loading={isRefreshing}
            >
              Run/Refresh Product IQ
            </Button>

            <div className="flex items-center gap-4">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                className="text-gray-500 hover:text-gray-700 hover:bg-transparent"
                onClick={handleResetContent}
                disabled={!selectedSupplierData || !overviewData || isBulkInsertMode}
              >
                Reset/Clear
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                className="!bg-[#102542] !border-[#102542] font-medium"
                onClick={handleSaveChanges}
                disabled={!isDirty || !selectedSupplierData || !overviewData || isLoadingOverview || isBulkInsertMode}
                loading={isSaving}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Footer for Close button */}
      <div className="py-3 px-6 flex justify-end bg-white border-t border-gray-200 rounded-b-lg">
        <Button
          type="default"
          className="bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 font-medium px-6"
          onClick={onCancel}
        >
          Close
        </Button>
      </div>
    </Modal>
  );
};

interface SupplierCardProps {
  viewInfo?: { showPartnerInfo: boolean; showNPIInfo: boolean; showToggle: boolean };
  setToggleValue?: (value: ((prevState: string) => string) | string) => void;
  toggleValue?: string;
  setViewInfo?: (value: { showPartnerInfo: boolean; showNPIInfo: boolean; showToggle: boolean }) => void;
  vendorsList?: Array<SupplierDynamicsVendor>;
  /**
   * Whether sub-tab changes write `?source=…&supplier_id=…&tab=…` to the browser URL. Defaults to true
   * (Supplier Dynamics page deep-linking). Pass false when embedded elsewhere (e.g. the client deal view)
   * so the host URL stays clean.
   */
  syncTabToUrl?: boolean;
}

export const SupplierCard: React.FC<SupplierCardProps & CardProps> = ({
  viewInfo,
  setToggleValue,
  toggleValue,
  setViewInfo,
  vendorsList,
  syncTabToUrl = true,
  ...rest
}): React.ReactElement => {
  // LOCAL STATE
  const [supplier, setSupplier] = useState<undefined | SupplierTitle>(undefined);
  const [profile, setProfile] = useState<string | undefined>(undefined);
  const [briefingReport, setBriefingReport] = useState<SupplierBriefingData>();
  const [activeKey, setActiveKey] = useState([1]);
  const [supplierRisk, setSupplierRisk] = useState<SupplierRisk>();
  const [searchParams] = useSearchParams();
  // When URL sync is off (e.g. embedded in the deal view) ignore any stale `tab` param and start on the default.
  const currentTab = searchParams.get('tab');
  const [selectedSupplierCardTab, setSelectedSupplierCardTab] = useState(
    syncTabToUrl
      ? (normalizeSupplierDynamicsTab(currentTab) ?? SUPPLIER_DYNAMICS_TAB_KEYS.SUPPLIER_IQ)
      : SUPPLIER_DYNAMICS_TAB_KEYS.SUPPLIER_IQ
  );
  const [quantumSupplier, setQuantumSupplier] = useState<undefined | QuantumVendorDetails>(undefined);
  const [loadingQuantumDetails, setLoadingQuantumDetails] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState<boolean>(false);
  const [editorSupplierId, setEditorSupplierId] = useState<number | undefined>(undefined);
  const [editorContent, setEditorContent] = useState<string>('');
  const [editorModalTitle, setEditorModalTitle] = useState<string>('Edit Product IQ');
  const [editorPanelTitle, setEditorPanelTitle] = useState<string>('Product IQ Information');
  const [isSavingSupplierIq, setIsSavingSupplierIq] = useState<boolean>(false);
  const [isSavingSupplierRisk, setIsSavingSupplierRisk] = useState<boolean>(false);
  const [isEditingRiskReport, setIsEditingRiskReport] = useState<boolean>(false);

  // API
  const { fetchQuantumVendorDetails, selectSupplierTitle } = useVendorApi();
  const { fetchSupplierBriefing, fetchSupplierRiskDetails, updateSupplierIqReport, updateSupplierRiskReport } =
    useSupplierDynamicsApi();

  /**
   * Handles tab click navigation and URL history update
   * @param {string} key - The tab key to navigate to
   * @returns {void}
   */
  const onTabClickHandler = (key: string): void => {
    const normalizedKey = normalizeSupplierDynamicsTab(key) ?? key;
    // Supplier Dynamics Plus / Quantum is currently disabled. Preserve this
    // legacy branch for future re-enable:
    // if (toggleValue == 'Partner Info' && quantumSupplier) {
    //   window.history.pushState(
    //     null,
    //     '',
    //     `${window.location.pathname}?source=quantum&domain=${selectedSupplier?.domain}&tab=${key}`
    //   );
    //   setSelectedSupplierCardTab(key);
    //   return;
    // }

    if (syncTabToUrl) {
      window.history.pushState(
        null,
        '',
        `${window.location.pathname}?source=npi&supplier_id=${selectedSupplier?.id}&tab=${normalizedKey}`
      );
    }
    setSelectedSupplierCardTab(normalizedKey);
  };

  /**
   * Toggles edit mode on and off
   * @returns {void}
   */
  const handleEditToggle = (): void => {
    setIsEditMode(!isEditMode);
  };

  // RECOIL STATE
  const selectedSupplier = useRecoilValue(selectedSupplierDynamicsVendorState);

  useEffect(() => {
    if (
      !currentTab ||
      !LEGACY_PURCHASE_CHARACTERISTICS_TABS.includes(
        currentTab as (typeof LEGACY_PURCHASE_CHARACTERISTICS_TABS)[number]
      )
    ) {
      return;
    }

    const normalizedParams = new URLSearchParams(searchParams);
    normalizedParams.set('tab', SUPPLIER_DYNAMICS_TAB_KEYS.PURCHASE_CHARACTERISTICS);
    if (!normalizedParams.get('source')) {
      normalizedParams.set('source', 'npi');
    }

    window.history.replaceState(null, '', `${window.location.pathname}?${normalizedParams.toString()}`);
  }, [currentTab, searchParams]);

  const refreshSupplierBriefing = async (supplierId: number): Promise<void> => {
    try {
      const supplierBriefing: SupplierBriefingData = await fetchSupplierBriefing(supplierId);
      setBriefingReport(supplierBriefing);
    } catch (err) {
      console.error('Error occurred while fetching supplier briefing:', err);
    }
  };

  const refreshSupplierRisk = async (supplierId: number): Promise<void> => {
    try {
      const supplierRisk: any | undefined = await fetchSupplierRiskDetails(supplierId);
      setSupplierRisk(supplierRisk);
    } catch (err) {
      console.error('Error occurred while fetching supplier risk details:', err);
    }
  };

  useEffect((): void => {
    if (!selectedSupplier) return;

    if (selectedSupplier.curatedBy === 'AI' || selectedSupplier.id <= 0) {
      setSupplier(undefined);
      setProfile(undefined);
      setBriefingReport(undefined);
      setSupplierRisk(undefined);
      setQuantumSupplier(undefined);
      setLoadingQuantumDetails(false);
      return;
    }

    if (selectedSupplier?.domain) {
      (async (): Promise<void> => {
        try {
          setLoadingQuantumDetails(true);
          if (selectedSupplier?.domain) {
            const newQuantamVendor: undefined | QuantumVendorDetails = await fetchQuantumVendorDetails(
              selectedSupplier?.domain
            );
            newQuantamVendor &&
              selectedSupplier?.domain &&
              setQuantumSupplier({ ...newQuantamVendor, vendor_domain: selectedSupplier?.domain });
            setLoadingQuantumDetails(false);
          }
        } catch (err) {
          console.error('Failed to fetch quantum vendor details:', err);
        }
      })();
      if (isNaN(selectedSupplier?.id) || selectedSupplier.id <= 0) return;
    } else {
      setQuantumSupplier(undefined);
    }

    (async (): Promise<void> => {
      if (isNaN(selectedSupplier?.id) || selectedSupplier.id <= 0) return;
      try {
        const newSupplier: undefined | SupplierTitle = await selectSupplierTitle(selectedSupplier.id);
        if (newSupplier) {
          setSupplier(newSupplier);
          //const [supplierCategory] = newSupplier.supplier_category.filter((item) => supplierCategoryId === item.id);
          setProfile(newSupplier.profile);
        }

        await refreshSupplierBriefing(selectedSupplier.id);

        const supplierRisk: any | undefined = await fetchSupplierRiskDetails(selectedSupplier.id);
        setSupplierRisk(supplierRisk);
      } catch (err) {
        console.error('Error occurred while fetching selected supplier details', err);
      }
    })();
    // eslint-disable-next-line
  }, [selectedSupplier]);

  useEffect(() => {
    const activeKey = localStorage.getItem('supplierIqCollapseKey');
    if (activeKey) {
      setActiveKey(JSON.parse(activeKey));
    }
  }, []);

  /**
   * Render the empty state component.
   * @type {{description: string, className: string}}
   * @property {string} description - The description of the empty state.
   * @property {string} className - The class name to be applied to the empty state component.
   */
  const renderEmptyState: React.ReactElement = <Empty description="No Data Found!" className="p-4"></Empty>;

  /**
   * Wraps content with edit mode styling and placeholder
   * @param {React.ReactElement} viewContent - The view mode content
   * @param {React.ReactElement} editContent - The edit mode content
   * @returns {React.ReactElement} The rendered content based on edit mode
   */
  const renderContentWithEditMode = (
    viewContent: React.ReactElement,
    editContent: React.ReactElement
  ): React.ReactElement => {
    if (isEditMode) {
      return (
        <div className="edit-mode-content p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="mb-4 text-sm font-semibold text-blue-900">Edit Mode - Modify the content below:</div>
          {editContent}
        </div>
      );
    }
    return viewContent;
  };

  /**
   * Toggles the open state of the component, activating or deactivating the key.
   * @function handleToggleOpen
   * @returns {void}
   */
  const handleToggleOpen = (): void => {
    const _activeKey = activeKey.length > 0 ? [] : [1];
    localStorage.setItem('supplierIqCollapseKey', JSON.stringify(_activeKey));
    setActiveKey(_activeKey);
  };

  const handleEditInformationClick = (content: string, header?: string): void => {
    if (supplier?.id) {
      setEditorSupplierId(supplier.id);
    } else {
      setEditorSupplierId(undefined);
    }
    setEditorContent(content);
    const sectionLabel = header ?? 'Edit Product IQ';
    const supplierLabel = supplier?.name ? `${supplier.name} : ` : '';
    setEditorModalTitle(sectionLabel);
    setEditorPanelTitle(`${supplierLabel}${sectionLabel}`);
    // Determine if we're editing risk report based on header
    setIsEditingRiskReport(sectionLabel.includes('Supplier Risk') || sectionLabel.includes('Risk'));
    setIsEditProductModalOpen(true);
  };

  /**
   * Handles closing the edit modal and refreshes supplier data
   * @returns {Promise<void>}
   */
  const handleCloseEditModal = async (): Promise<void> => {
    setIsEditProductModalOpen(false);
    setIsEditingRiskReport(false);
  };

  /**
   * Handles saving the edited Supplier IQ content
   * @param {string} content - The edited HTML content from the editor
   * @returns {Promise<void>}
   */
  const handleSaveSupplierIq = async (content: string): Promise<void> => {
    const supplierId = editorSupplierId ?? supplier?.id;
    if (!supplierId) {
      message.error('No supplier selected');
      return;
    }

    setIsSavingSupplierIq(true);
    try {
      // Convert HTML to markdown before sending to API
      console.log('content', content);
      const markdownContent = turndownService.turndown(normalizeTableHtml(content));

      // If API call succeeds (no exception thrown), consider it successful
      await updateSupplierIqReport(supplierId, markdownContent);
      message.success('Supplier IQ report updated successfully');
      setBriefingReport((prev) =>
        prev
          ? {
              ...prev,
              briefing: markdownContent
            }
          : prev
      );
      setEditorContent(content);
      await handleCloseEditModal();
    } catch (error) {
      console.error('Failed to update supplier IQ report:', error);
      message.error('Failed to update supplier IQ report. Please try again.');
    } finally {
      setIsSavingSupplierIq(false);
    }
  };

  const parseHtmlToRiskReport = (
    htmlContent: string,
    originalReport: SupplierRiskData[]
  ): Array<{ score: number; title: string; payload: string }> => {
    if (!htmlContent || !htmlContent.trim()) {
      // Return original structure if no content
      return originalReport.map((item) => ({
        score: item.score,
        title: item.title,
        payload: item.payload
      }));
    }

    const reportArray: Array<{ score: number; title: string; payload: string }> = [];

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent.trim();

    const sections: Array<{ title: string; content: string }> = [];
    const childNodes = Array.from(tempDiv.childNodes);

    // Walk through all child nodes and group by headings
    let currentTitle = '';
    let currentContentNodes: Node[] = [];

    childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName?.toUpperCase() || '';

        // Check if it's a heading
        if (/^H[1-6]$/.test(tagName)) {
          // Save previous section if exists
          if (currentTitle) {
            const contentHtml = currentContentNodes
              .map((n) => {
                if (n.nodeType === Node.ELEMENT_NODE) {
                  return (n as Element).outerHTML;
                } else if (n.nodeType === Node.TEXT_NODE) {
                  const text = n.textContent?.trim() || '';
                  return text ? `<p>${text}</p>` : '';
                }
                return '';
              })
              .filter(Boolean)
              .join('');

            const contentMarkdown = contentHtml.trim() ? turndownService.turndown(contentHtml.trim()) : '';

            sections.push({ title: currentTitle, content: contentMarkdown });
          }

          // Start new section
          currentTitle = element.textContent?.trim() || '';
          currentContentNodes = [];
        } else {
          // Add to current section's content
          currentContentNodes.push(node);
        }
      } else if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim() || '';
        if (text) {
          currentContentNodes.push(node);
        }
      }
    });

    // Add last section
    if (currentTitle) {
      const contentHtml = currentContentNodes
        .map((n) => {
          if (n.nodeType === Node.ELEMENT_NODE) {
            return (n as Element).outerHTML;
          } else if (n.nodeType === Node.TEXT_NODE) {
            const text = n.textContent?.trim() || '';
            return text ? `<p>${text}</p>` : '';
          }
          return '';
        })
        .filter(Boolean)
        .join('');

      const contentMarkdown = contentHtml.trim() ? turndownService.turndown(contentHtml.trim()) : '';

      sections.push({ title: currentTitle, content: contentMarkdown });
    }

    // Fallback: if no headings found, treat entire content as single section
    if (sections.length === 0 && originalReport.length > 0) {
      const contentMarkdown = turndownService.turndown(htmlContent);
      sections.push({
        title: originalReport[0].title,
        content: contentMarkdown
      });
    }

    // Map parsed sections to report items, ALWAYS using original titles (titles are not editable)
    // Match sections by index order to preserve the original report structure
    sections.forEach((section, index) => {
      // Always use original report item by index - titles cannot be changed by users
      if (index >= originalReport.length) {
        // If more sections than original reports, skip extra sections
        return;
      }

      const originalItem = originalReport[index];

      // Always use original title - ignore any edits to heading text
      const score = originalItem.score;
      const finalTitle = originalItem.title; // Original title, never changed
      const finalPayload = section.content.trim() || originalItem.payload; // Only payload can be edited

      reportArray.push({
        score,
        title: finalTitle,
        payload: finalPayload
      });
    });

    // Ensure all original reports are included, even if user removed some sections
    // Add any missing reports from original data
    if (reportArray.length < originalReport.length) {
      originalReport.forEach((originalItem, index) => {
        if (index >= reportArray.length) {
          // Add missing reports with their original payload
          reportArray.push({
            score: originalItem.score,
            title: originalItem.title,
            payload: originalItem.payload
          });
        }
      });
    }

    // If no sections were parsed, return original structure
    if (reportArray.length === 0 && originalReport.length > 0) {
      return originalReport.map((item) => ({
        score: item.score,
        title: item.title,
        payload: item.payload
      }));
    }

    return reportArray;
  };

  const handleSaveSupplierRisk = async (content: string): Promise<void> => {
    const supplierId = editorSupplierId ?? supplier?.id;
    if (!supplierId) {
      message.error('No supplier selected');
      return;
    }

    if (!supplierRisk?.report || supplierRisk.report.length === 0) {
      message.error('No risk report data available');
      return;
    }

    setIsSavingSupplierRisk(true);
    try {
      const reportArray = parseHtmlToRiskReport(content, supplierRisk.report);

      if (reportArray.length === 0) {
        message.error('Failed to parse risk report content');
        setIsSavingSupplierRisk(false);
        return;
      }

      // If API call succeeds (no exception thrown), consider it successful
      await updateSupplierRiskReport(supplierId, reportArray);
      message.success('Supplier Risk report updated successfully');
      setSupplierRisk((prev) =>
        prev
          ? {
              ...prev,
              report: reportArray
            }
          : prev
      );
      setEditorContent(content);
      await handleCloseEditModal();
    } catch (error) {
      console.error('Failed to update supplier risk report:', error);
      message.error('Failed to update supplier risk report. Please try again.');
    } finally {
      setIsSavingSupplierRisk(false);
    }
  };

  const supplierOptions = useMemo<Array<{ id: number; name: string }>>(() => {
    if (vendorsList && vendorsList.length > 0) {
      return vendorsList.map((vendor) => ({ id: vendor.id, name: vendor.name }));
    }
    if (supplier?.id && supplier?.name) {
      return [{ id: supplier.id, name: supplier.name }];
    }
    return [];
  }, [vendorsList, supplier]);

  const menuItems = [
    {
      key: SUPPLIER_DYNAMICS_TAB_KEYS.SUPPLIER_IQ,
      label: 'Supplier IQ™',
      children: renderContentWithEditMode(
        briefingReport?.briefing ? (
          <SupplierIQContent
            briefingReport={briefingReport}
            supplier={supplier}
            handleToggleOpen={handleToggleOpen}
            activeKey={activeKey}
            onEditClick={handleEditInformationClick}
          />
        ) : (
          renderEmptyState
        ),
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit Supplier IQ™ Data</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for supplier IQ data here</p>
        </div>
      )
    },
    {
      key: SUPPLIER_DYNAMICS_TAB_KEYS.SUPPLIER_RISK,
      label: 'Supplier Risk',
      children: renderContentWithEditMode(
        supplierRisk?.report ? (
          <SupplierRiskContent
            id={supplierRisk?.id}
            riskReport={supplierRisk?.report}
            handleToggleOpen={handleToggleOpen}
            activeKey={activeKey}
            supplier={supplier}
            onEditClick={handleEditInformationClick}
          />
        ) : (
          renderEmptyState
        ),
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit Supplier Risk Data</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for supplier risk here</p>
        </div>
      )
    },
    ...(ACLCheck(['read:product_iq'])
      ? [
          {
            key: 'productIQ',
            label: 'Product IQ',
            children: <ProductIQProductsTab vendorDomain={supplier?.domain} />
          }
        ]
      : []),
    {
      key: SUPPLIER_DYNAMICS_TAB_KEYS.PURCHASE_CHARACTERISTICS,
      label: `Purchase Characteristics`,
      children: renderContentWithEditMode(
        <AboutSupplier
          profile={profile}
          supplier={supplier}
          selectedSupplierCardTab={selectedSupplierCardTab}
          setSelectedSupplierCardTab={setSelectedSupplierCardTab}
          syncTabToUrl={syncTabToUrl}
        />,
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit {selectedSupplier?.name} Information</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for supplier information here</p>
        </div>
      )
    },
    {
      key: SUPPLIER_DYNAMICS_TAB_KEYS.RESOURCES,
      label: 'NPI Resources',
      children: renderContentWithEditMode(
        <>
          <Typography.Title level={5} className="!font-normal !pt-3 !mb-0 opacity-60">
            IT Sourcing Insights
          </Typography.Title>
          <Typography.Title level={4} className="!font-light !m-0 opacity-80">
            Stay on top of what&apos;s new, what&apos;s changing and what to look out for
          </Typography.Title>
          <ArticleList vendorId={supplier?.id} hideTabs={true} hideActions={true} />
        </>,
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit NPI Resources</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for NPI resources here</p>
        </div>
      )
    },
    {
      key: SUPPLIER_DYNAMICS_TAB_KEYS.NEWS,
      label: 'Recent News',
      children: renderContentWithEditMode(
        ACLCheck(['read:supplier_news']) ? (
          <div className="mt-4">
            <h2 className="mb-1">{selectedSupplier?.name} in the News - Past 30 Days</h2>
            <Divider />
            <SupplierNews showNewsDetailsCard={true} />
          </div>
        ) : (
          <Empty
            description={`NPI's news source is temporarily down for maintenance.`}
            className="p-4 text-lg text-slate-600"
          />
        ),
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit Recent News</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for recent news here</p>
        </div>
      )
    },
    {
      key: SUPPLIER_DYNAMICS_TAB_KEYS.NOTES,
      label: 'Notes',
      children: renderContentWithEditMode(
        <DealNotes supplier_id={supplier?.id} />,
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit Notes</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for notes here</p>
        </div>
      )
    }
  ];

  const quantumMenuItems = [
    {
      key: 'about',
      label: `About ${quantumSupplier?.name}`,
      children: renderContentWithEditMode(
        <Skeleton loading={loadingQuantumDetails}>
          <AboutQuantumSupplier
            quantumSupplier={quantumSupplier}
            setSelectedSupplierCardTab={setSelectedSupplierCardTab}
            toggleValue={toggleValue}
            selectedSupplierCardTab={selectedSupplierCardTab}
            setViewInfo={setViewInfo}
            vendorsList={vendorsList}
          />
        </Skeleton>,
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit {quantumSupplier?.name} Information</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for quantum supplier information here</p>
        </div>
      )
    },
    ENABLE_SD_PRODUCTS
      ? {
          key: 'products',
          label: `Products`,
          children: renderContentWithEditMode(
            <SupplierProducts
              quantumSupplier={quantumSupplier}
              setSelectedSupplierCardTab={setSelectedSupplierCardTab}
              setViewInfo={setViewInfo}
              vendorsList={vendorsList}
            />,
            <div className="placeholder-edit">
              <Typography.Title level={4}>Edit Products</Typography.Title>
              <p className="text-slate-600">Add your custom edit form for products here</p>
            </div>
          )
        }
      : null,
    {
      key: 'news',
      label: 'Recent News',
      children: renderContentWithEditMode(
        ACLCheck(['read:supplier_news']) ? (
          <div className="mt-4">
            <h2 className="mb-1">{selectedSupplier?.name} in the News - Past 30 Days</h2>
            <Divider />
            <SupplierNews showNewsDetailsCard={true} />
          </div>
        ) : (
          renderEmptyState
        ),
        <div className="placeholder-edit">
          <Typography.Title level={4}>Edit Recent News</Typography.Title>
          <p className="text-slate-600">Add your custom edit form for recent news here</p>
        </div>
      )
    }
  ];

  /**
   * Render tabs component.
   * @returns {React.ReactElement} The rendered tabs component.
   */
  const renderTabs = (): React.ReactElement => (
    <div style={{ marginBottom: panelMarginBottom }}>
      {/* Supplier Dynamics Plus / Quantum is currently disabled. Preserve this
          legacy tab selection logic for future re-enable:
          defaultActiveKey={
            currentSource === 'quantum'
              ? currentTab || 'about'
              : currentTab
                ? normalizeSupplierDynamicsTab(currentTab)
                : (!viewInfo?.showToggle && viewInfo?.showPartnerInfo) || toggleValue == 'Partner Info'
                  ? 'about'
                : SUPPLIER_DYNAMICS_TAB_KEYS.SUPPLIER_IQ
          }
          items={
            (!viewInfo?.showToggle && viewInfo?.showPartnerInfo) || toggleValue == 'Partner Info'
              ? quantumMenuItems.filter((item): item is NonNullable<typeof item> => item !== null)
              : menuItems
          }
       */}
      <Tabs
        defaultActiveKey={normalizeSupplierDynamicsTab(currentTab) ?? SUPPLIER_DYNAMICS_TAB_KEYS.SUPPLIER_IQ}
        activeKey={selectedSupplierCardTab}
        items={menuItems}
        size="large"
        onChange={(key) => onTabClickHandler(key)}
      />
    </div>
  );

  return (
    <>
      <Card
        className="supplier-card"
        title={
          <SupplierCardTitle
            onSupplierUpdate={setSupplier}
            supplier={supplier}
            quantumSupplier={quantumSupplier}
            viewInfo={viewInfo}
            setToggleValue={setToggleValue}
            toggleValue={toggleValue}
          />
        }
        {...rest}
      >
        <div style={{ marginBottom: panelMarginBottom }} className="panel">
          <div className="panel-header">About {selectedSupplier?.name}</div>
          <div style={{ fontSize: 16 }}>{profile}</div>
        </div>
        <Row gutter={24}>
          <Col span={24}>{renderTabs()}</Col>
        </Row>
      </Card>
      <SupplierContentEditorModal
        open={isEditProductModalOpen}
        onCancel={handleCloseEditModal}
        suppliers={supplierOptions}
        selectedSupplierId={editorSupplierId ?? supplier?.id}
        initialContent={editorContent}
        headerTitle={editorModalTitle}
        panelTitle={editorPanelTitle}
        onSave={
          isEditingRiskReport
            ? handleSaveSupplierRisk
            : editorModalTitle.includes('Supplier IQ') || editorModalTitle.includes('Edit Supplier IQ')
              ? handleSaveSupplierIq
              : undefined
        }
        isSaving={isEditingRiskReport ? isSavingSupplierRisk : isSavingSupplierIq}
      />
    </>
  );
};
